import { useEffect, useId, useMemo, useState } from 'react';
import { RetryableError } from '../../../shared/RetryableError';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { industryTagLabel, listChangesFor, type IndustryChangePreview, type PreviewRow, type SetIndustryProfileInput } from '../types';
import '../healthcare-rates.css';

export interface IndustryChangePreviewPanelProps {
  /** The answer on screen, not yet saved. */
  input: SetIndustryProfileInput;
  /** The work types the company can see (EPN defaults and its own; inactive ones are ignored). */
  serviceTypes: readonly PreviewRow[];
  /** The worker categories, or null when they could not be read (then only the work types are compared). */
  disciplines: readonly PreviewRow[] | null;
}

const DEBOUNCE_MS = 400;

/** The industries a list of tags stands for, in words ("Construction", "Hospice, Health Care and Social Assistance"), or null for no suggestions. */
function describeTags(tags: readonly string[]): string | null {
  if (tags.length === 0) return null;
  const sectors = tags.filter((t) => t.startsWith('Naics')).map(industryTagLabel);
  const templates = tags.filter((t) => !t.startsWith('Naics'));
  // Every healthcare type at once is "Health Care and Social Assistance" already; name a type only when one or a few were chosen.
  const named = templates.length > 0 && templates.length < 7 ? templates.map(industryTagLabel) : [];
  return [...named, ...sectors].join(', ');
}

type Outcome = { key: string; preview: IndustryChangePreview; error: null } | { key: string; preview: null; error: string };

function Names({ label, names, testId }: { label: string; names: string[]; testId: string }) {
  if (names.length === 0) return null;
  return (
    <details className="hdc-preview__names" data-testid={testId}>
      <summary>{label} ({names.length})</summary>
      <ul>{names.map((n) => <li key={n}>{n}</li>)}</ul>
    </details>
  );
}

/**
 * "What would change if you save this": a read-only preview of how the answer on screen would change the suggestions, asked of the server before anything is saved. It shows
 * which EPN default worker categories and work types would be listed first and which would stop being, from the lists the page already has, using the rule every list uses.
 * Suggestions only: nothing is deleted, a company's own items are always shown, and no rate, employee assignment or pay changes.
 */
export function IndustryChangePreviewPanel({ input, serviceTypes, disciplines }: IndustryChangePreviewPanelProps) {
  const uid = useId();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The version a save needs is not part of a preview, so it never decides when to ask again.
  const key = useMemo(() => JSON.stringify([input.primary, input.additional, attempt]), [input.primary, input.additional, attempt]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      healthcareRatesGateway
        .previewIndustryChange({ primary: input.primary, additional: input.additional }, controller.signal)
        .then((preview) => { if (!controller.signal.aborted) setOutcome({ key, preview, error: null }); })
        .catch((err: unknown) => { if (!controller.signal.aborted) setOutcome({ key, preview: null, error: healthcareErrorMessage(err, 'Could not check what would change.') }); });
    }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` already stands for primary, additional and attempt
  }, [key]);

  const current = outcome?.key === key ? outcome : null;
  const preview = current?.preview ?? null;
  const categories = useMemo(
    () => (preview && disciplines ? listChangesFor(disciplines, preview.currentSuggestionTags, preview.proposedSuggestionTags) : null),
    [preview, disciplines],
  );
  const types = useMemo(() => (preview ? listChangesFor(serviceTypes, preview.currentSuggestionTags, preview.proposedSuggestionTags) : null), [preview, serviceTypes]);

  return (
    <section className="hdc-preview" aria-labelledby={`${uid}-title`} data-testid="industry-preview">
      <h4 id={`${uid}-title`}>What would change if you save this</h4>
      <p className="hdc-note">Nothing has been saved. Only the suggestions change: your own worker categories and work types are always shown, nothing is deleted, and no rate, employee assignment or pay changes.</p>

      <div aria-live="polite">
        {current === null && <p className="muted" role="status">Checking what would change…</p>}
        {current?.error && <RetryableError message={current.error} onRetry={() => setAttempt((n) => n + 1)} />}

        {preview && types && (
          <>
            {!preview.suggestionsChange ? (
              <p data-testid="industry-preview-same">The suggestions would stay the same, so the lists would look exactly as they do now.</p>
            ) : (
              <p data-testid="industry-preview-tags">
                Suggestions would follow <strong>{describeTags(preview.proposedSuggestionTags) ?? 'no industry, so every EPN default is listed'}</strong>
                {' '}instead of <strong>{describeTags(preview.currentSuggestionTags) ?? 'no industry, so every EPN default is listed'}</strong>.
              </p>
            )}

            {preview.suggestionsChange && (
              <>
                <div className="hdc-preview__group" data-testid="industry-preview-categories">
                  <h5>Worker categories</h5>
                  {categories === null ? (
                    <p className="muted">The worker categories could not be loaded, so they are not compared here.</p>
                  ) : categories.newlyListed.length === 0 && categories.noLongerListed.length === 0 ? (
                    <p>No EPN default would move.</p>
                  ) : (
                    <>
                      <Names label="Would be listed first" names={categories.newlyListed} testId="industry-preview-categories-added" />
                      <Names label="Would no longer be listed first" names={categories.noLongerListed} testId="industry-preview-categories-removed" />
                    </>
                  )}
                </div>
                <div className="hdc-preview__group" data-testid="industry-preview-types">
                  <h5>Work types</h5>
                  {types.newlyListed.length === 0 && types.noLongerListed.length === 0 ? (
                    <p>No EPN default would move.</p>
                  ) : (
                    <>
                      <Names label="Would be listed first" names={types.newlyListed} testId="industry-preview-types-added" />
                      <Names label="Would no longer be listed first" names={types.noLongerListed} testId="industry-preview-types-removed" />
                    </>
                  )}
                </div>
                <p className="hdc-note">An item that would no longer be listed first is still available: tick “Show worker categories for every industry” when you add one, or untick “Only defaults suggested for …” in the lists below.</p>
              </>
            )}

            {preview.currentIsHealthcare !== preview.proposedIsHealthcare && (
              <p className="hdc-note" data-testid="industry-preview-healthcare">
                {preview.proposedIsHealthcare
                  ? 'The Medicare and reimbursement notes, which appear only for healthcare companies, would be shown.'
                  : 'The Medicare and reimbursement notes, which appear only for healthcare companies, would no longer be shown.'}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
